import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Grid,
    TextField,
    MenuItem,
    Card,
    CardContent,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    useTheme,
    alpha,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import {
    Calculate as CalculateIcon,
    SettingsSuggest as StrategyIcon,
    Warehouse as WarehouseIcon,
    LocalShipping as ShippingIcon,
    Map as MapIcon,
    TrendingUp as TrendingIcon,
    Circle as CircleIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import routingService from '../../services/routingService';
import GlassPanel from '../common/GlassPanel';
import StatsCard from '../common/StatsCard';
import {
    SUPPORTED_CURRENCIES,
    getCurrencySymbol,
} from '@/utils/invoice-pdf-general-section-utilities';

const OrderRoutingControl = () => {
    const theme = useTheme();
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [strategies, setStrategies] = useState(['OPTIMAL_STOCK']);
    const [selectedCurrency, setSelectedCurrency] = useState('EUR');

    // Format currency with selected currency symbol
    const formatCurrency = useCallback((amount) => {
        const numericAmount = Number(amount) || 0;
        const formattedNumber = numericAmount.toLocaleString(i18n?.language || 'en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return `${getCurrencySymbol(selectedCurrency)}${formattedNumber}`;
    }, [i18n?.language, selectedCurrency]);
    
    // Simulation Form
    const [request, setRequest] = useState({
        strategy: 'OPTIMAL_STOCK',
        destination_zip: '10001',
        items: [
            { product_id: 'P-1001', quantity: 5 },
            { product_id: 'P-2002', quantity: 2 }
        ]
    });

    const [result, setResult] = useState(null);

    useEffect(() => {
        // Load capabilities
        routingService.getStrategies()
            .then(setStrategies)
            .catch((err) => {
                console.error("Failed to load strategies:", err);
                setError("Could not load routing strategies from backend.");
            });
    }, []);

    const handleCalculate = async () => {
        setLoading(true);
        setError(null);
        setResult(null);
        
        try {
            const data = await routingService.calculateRoute({
                items: request.items,
                destination_address: { zip: request.destination_zip },
                strategy: request.strategy
            });
            setResult(data);
        } catch (error) {
            console.error("Routing Calculation Failed:", error);
            setError(error.message || "Routing calculation failed. Ensure TMS service is online.");
            // STRICT NO MOCK POLICY: Do not set fake result.
        } finally {
            setLoading(false);
        }
    };

    const updateItem = (index, field, value) => {
        const newItems = [...request.items];
        newItems[index] = { ...newItems[index], [field]: value };
        setRequest({ ...request, items: newItems });
    };

    const addItem = () => {
        setRequest({
            ...request,
            items: [...request.items, { product_id: '', quantity: 1 }]
        });
    };

    const removeItem = (index) => {
        const newItems = request.items.filter((_, i) => i !== index);
        setRequest({ ...request, items: newItems });
    };

    return (
        <GlassPanel sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
                        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">{t('tms.routing.title', 'Order Routing Intelligence')}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t('tms.routing.subtitle', 'Configure logic for multi-warehouse fulfillment')}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <FormControl size="small" sx={{ minWidth: 100 }}>
                                    <InputLabel>{t('common.currency', 'Currency')}</InputLabel>
                                    <Select
                                        value={selectedCurrency}
                                        label={t('common.currency', 'Currency')}
                                        onChange={(e) => setSelectedCurrency(e.target.value)}
                                    >
                                        {SUPPORTED_CURRENCIES.map((currency) => (
                                            <MenuItem key={currency} value={currency}>
                                                {getCurrencySymbol(currency)} {currency}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <Chip 
                                    icon={<StrategyIcon />} 
                                    label={`${t('tms.routing.activeStrategy', 'Active Strategy')}: ${request.strategy}`} 
                                    color="primary" 
                                    variant="outlined" 
                                />
                            </Box>
                        </Box>

            <Grid container sx={{ flexGrow: 1, height: '100%' }}>
                {/* Configuration Panel */}
                <Grid item xs={12} md={4} sx={{ borderRight: '1px solid', borderColor: 'divider', p: 2, overflowY: 'auto' }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalculateIcon fontSize="small" /> Simulation Parameters
                    </Typography>
                    
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                        <TextField
                            label="Routing Strategy"
                            select
                            value={request.strategy}
                            onChange={(e) => setRequest({ ...request, strategy: e.target.value })}
                            fullWidth
                            size="small"
                        >
                            {strategies.map(s => (
                                <MenuItem key={s} value={s}>{s.replace('_', ' ')}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Destination Zip Code"
                            value={request.destination_zip}
                            onChange={(e) => setRequest({ ...request, destination_zip: e.target.value })}
                            fullWidth
                            size="small"
                        />

                        <Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                <Typography variant="caption">Order Items</Typography>
                                <Button size="small" onClick={addItem}>+ Add Item</Button>
                            </Box>
                            {request.items.map((item, index) => (
                                <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                    <TextField 
                                        placeholder="Product ID" 
                                        value={item.product_id}
                                        onChange={(e) => updateItem(index, 'product_id', e.target.value)}
                                        size="small"
                                        fullWidth
                                    />
                                    <TextField 
                                        type="number"
                                        placeholder="Qty" 
                                        value={item.quantity}
                                        onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                                        size="small"
                                        sx={{ width: 80 }}
                                    />
                                    {request.items.length > 1 && (
                                        <Button color="error" size="small" sx={{ minWidth: 30 }} onClick={() => removeItem(index)}>×</Button>
                                    )}
                                </Box>
                            ))}
                        </Box>

                        <Button 
                            variant="contained" 
                            startIcon={loading ? <CircularProgress size={20} color="inherit"/> : <CalculateIcon />}
                            onClick={handleCalculate}
                            disabled={loading}
                        >
                            Run Simulation
                        </Button>
                    </Box>
                </Grid>

                {/* Results Panel */}
                <Grid item xs={12} md={8} sx={{ p: 3, bgcolor: alpha(theme.palette.background.default, 0.4), height: '100%', overflowY: 'auto' }}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {!result && !error ? (
                        <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', opacity: 0.5 }}>
                            <MapIcon sx={{ fontSize: 60, mb: 2 }} />
                            <Typography>Run a simulation to see routing logic in action</Typography>
                        </Box>
                    ) : result ? (
                        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={4}>
                                                                        <StatsCard 
                                                                            title={t('tms.routing.totalCost', 'Total Cost')} 
                                                                            value={formatCurrency(result.total_cost)} 
                                                                            icon={TrendingIcon} 
                                                                            color={theme.palette.success.main}
                                                                        />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <StatsCard 
                                        title="Warehouses" 
                                        value={result.packages?.length || 1} 
                                        icon={WarehouseIcon} 
                                        color={theme.palette.primary.main}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <StatsCard 
                                        title="Est. Delivery" 
                                        value={result.estimated_delivery} 
                                        icon={ShippingIcon} 
                                        color={theme.palette.info.main}
                                    />
                                </Grid>
                            </Grid>

                            <Typography variant="h6" gutterBottom>Fulfillment Plan</Typography>
                            {result.packages?.map((pkg, i) => (
                                <Card key={i} sx={{ mb: 2, overflow: 'visible' }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <WarehouseIcon color="action" />
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    Source: {pkg.warehouse_id}
                                                </Typography>
                                            </Box>
                                            <Chip label={pkg.carrier} size="small" color="info" />
                                        </Box>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Product</TableCell>
                                                    <TableCell align="right">Qty</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {pkg.items.map((item, j) => (
                                                    <TableRow key={j}>
                                                        <TableCell>{item.product_id}</TableCell>
                                                        <TableCell align="right">{item.quantity}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            ))}

                            <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Decision Logic Log</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                {result.logs?.map((log, i) => (
                                    <Box key={i} sx={{ display: 'flex', gap: 2, pb: 2, position: 'relative' }}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                             <CircleIcon sx={{ fontSize: 12, color: 'secondary.main', zIndex: 1 }} />
                                             {i < result.logs.length - 1 && (
                                                <Box sx={{ width: 1, height: '100%', bgcolor: 'divider', position: 'absolute', top: 12, left: 5.5 }} />
                                             )}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">{log}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ) : null}
                </Grid>
            </Grid>
        </GlassPanel>
    );
};

export default React.memo(OrderRoutingControl);
